import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  Wrench,
  Volume2,
  ArrowRight,
  Cog,
  Hammer,
  Settings,
  MessageSquare,
  Globe,
  Target,
  Lightbulb
} from 'lucide-react';

const GENERAL_VOCAB = [
  { de: 'das Bauteil', en: 'part / component' },
  { de: 'die technische Zeichnung', en: 'technical drawing' },
  { de: 'der Motor', en: 'engine / motor' },
  { de: 'die Baugruppe', en: 'assembly' },
  { de: 'der Werkstoff', en: 'material' },
  { de: 'die Stückliste', en: 'bill of materials (BOM)' },
  { de: 'die Wartung', en: 'servicing / maintenance' },
  { de: 'das Lastenheft', en: 'requirement specification' },
];

const MECHANICS_VOCAB = [
  { de: 'das Drehmoment', en: 'torque' },
  { de: 'die Reibung', en: 'friction' },
  { de: 'der Verschleiß', en: 'wear and tear' },
  { de: 'die Übertragung', en: 'transmission / transfer' },
  { de: 'die Steuerung', en: 'control system' },
  { de: 'die Versorgung', en: 'supply' },
];

const COMMS = [
  { de: 'Das Bauteil passt nicht in die Baugruppe.', en: 'The component does not fit into the assembly.' },
  { de: 'Welchen Werkstoff benutzen wir?', en: 'Which material are we using?' },
  { de: 'Funktioniert der Sensor?', en: 'Is the sensor working?' },
  { de: 'Wir brauchen die Freigabe des Kunden.', en: 'We need approval from the customer.' },
  { de: 'Weißt du, wo das Lastenheft ist?', en: 'Do you know where the requirement specification is?' },
  { de: 'Könntest du mir kurz helfen?', en: 'Could you help me for a moment?' },
];

const EngineeringCareerPage = () => {
  const [activeVocab, setActiveVocab] = useState('general');
  const [playingAudio, setPlayingAudio] = useState(null);

  const speak = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'de-DE';
      u.rate = 0.9;
      u.onstart = () => setPlayingAudio(id);
      u.onend = () => setPlayingAudio(null);
      window.speechSynthesis.speak(u);
    }
  };

  const vocab = activeVocab === 'general' ? GENERAL_VOCAB : MECHANICS_VOCAB;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="engineering-career-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Wrench className="w-4 h-4" />
            Career Path
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Engineering &amp; Trades
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            Maschinenbau &amp; Handwerk — Germany's industrial backbone, and a sector with a desperate skills shortage.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8">
            <p className="text-babyblue-700 text-lg leading-relaxed mb-3">
              This move is a smart one. Germany is one of the countries with the <strong>highest demand for skilled engineers</strong> in Europe — especially mechanical, electrical, automotive and process engineering.
            </p>
            <p className="text-babyblue-700 text-lg leading-relaxed">
              Three factors decide whether you'll succeed: <strong>legal recognition</strong> of your diploma, <strong>language skills</strong>, and <strong>cultural fit</strong> with German engineering teams.
            </p>
          </div>
        </div>
      </section>

      {/* Big Boss to Beat */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-orange-300 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-200 rounded-xl flex-shrink-0">
                <Target className="w-7 h-7 text-orange-800" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-2xl font-bold text-orange-900 mb-3">
                  Big Boss to Beat: Language
                </h2>
                <p className="text-orange-900 leading-relaxed mb-4">
                  <strong>Tech-side engineering</strong> (IT, software, data) often runs in English.
                  But <strong>mechanical engineering</strong> on the factory floor is a different world entirely — German is non-negotiable.
                </p>
                <div className="bg-white rounded-xl p-4 border border-orange-200 mb-4">
                  <p className="text-orange-900 font-bold mb-1 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                    Goal
                  </p>
                  <p className="text-orange-900">
                    Aim for a <strong>solid B2</strong>. <strong>C1</strong> if you want to go higher than team-lead.
                  </p>
                </div>
                <p className="text-orange-900 leading-relaxed">
                  Your daily communication baseline is with <strong>blue-collar workers</strong> — technicians, mechanics, machine operators on the factory floor. Many of them <strong>don't speak English</strong>. That's where your German proficiency becomes the bridge between you and your team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fachsprache intro */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-3 py-1.5 rounded-full text-xs font-semibold mb-3">
              <Cog className="w-3.5 h-3.5" />
              Fachsprache
            </div>
            <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2">
              Engineering Vocabulary
            </h2>
            <p className="text-babyblue-600">Technical jargon — the <em>Fachsprache</em>. Click any word to hear it.</p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-8">
            <button
              onClick={() => setActiveVocab('general')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeVocab === 'general'
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-babyblue-50 text-babyblue-700 border border-babyblue-200 hover:bg-babyblue-100'
              }`}
              data-testid="vocab-tab-general"
            >
              <Hammer className="w-4 h-4" />
              General Engineering
            </button>
            <button
              onClick={() => setActiveVocab('mechanics')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeVocab === 'mechanics'
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-babyblue-50 text-babyblue-700 border border-babyblue-200 hover:bg-babyblue-100'
              }`}
              data-testid="vocab-tab-mechanics"
            >
              <Settings className="w-4 h-4" />
              Mechanics
            </button>
          </div>

          {/* Vocab grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="engineering-vocab-grid">
            {vocab.map((word, i) => (
              <button
                key={`${activeVocab}-${i}`}
                onClick={() => speak(word.de, `voc-${activeVocab}-${i}`)}
                className="bg-babyblue-50 rounded-xl p-4 border border-babyblue-200 hover:border-primary hover:shadow-md transition-all text-left group flex items-start justify-between gap-3"
                data-testid={`engineering-vocab-${activeVocab}-${i}`}
              >
                <div>
                  <p className="font-bold text-babyblue-800">{word.de}</p>
                  <p className="text-sm text-babyblue-600">{word.en}</p>
                </div>
                <div className={`p-2 rounded-full flex-shrink-0 transition-all ${
                  playingAudio === `voc-${activeVocab}-${i}` ? 'bg-primary text-white' : 'bg-white text-primary opacity-0 group-hover:opacity-100'
                }`}>
                  <Volume2 className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Communication phrases */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            On-the-Floor Communication
          </h2>
          <p className="text-babyblue-600 text-center mb-8 flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Phrases you'll use every single day
          </p>

          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
            <div className="space-y-2">
              {COMMS.map((phrase, i) => (
                <button
                  key={i}
                  onClick={() => speak(phrase.de, `comm-${i}`)}
                  className="w-full text-left bg-babyblue-50 hover:bg-babyblue-100 p-4 rounded-lg border border-babyblue-200 transition-all flex items-center justify-between"
                  data-testid={`engineering-comm-${i}`}
                >
                  <div>
                    <p className="font-semibold text-babyblue-800">{phrase.de}</p>
                    <p className="text-sm text-babyblue-600 italic">{phrase.en}</p>
                  </div>
                  <Volume2 className={`w-4 h-4 flex-shrink-0 ${playingAudio === `comm-${i}` ? 'text-primary animate-pulse' : 'text-babyblue-400'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visa note */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 flex items-start gap-4" data-testid="engineering-visa-note">
            <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
              <Globe className="w-7 h-7 text-blue-700" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-2">
                Engineering = Engpassberuf
              </h3>
              <p className="text-babyblue-700 leading-relaxed">
                Engineering is on Germany's official shortage-occupation list, so the EU Blue Card threshold is lowered to <strong>€45,934.20/year (2025)</strong> and Anerkennung is straightforward for EU/EEA degrees. Non-EU degrees go through the <em>Zentralstelle für ausländisches Bildungswesen</em> (ZAB) for recognition.{' '}
                <Link to="/integration/visa" className="text-primary font-semibold underline">Full visa guide →</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Explore Other Career Paths</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '💻', title: 'IT & Technology', path: '/careers/it' },
              { icon: '🏢', title: 'Business & Office', path: '/careers/business' },
              { icon: '🚚', title: 'Logistics', path: '/careers/logistics' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="bg-white rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EngineeringCareerPage;
